import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useToast,
  Text,
  HStack,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
} from '@chakra-ui/react';
import { FiMoreVertical, FiDollarSign, FiUserX, FiUserCheck } from 'react-icons/fi';
import { useAuth, authApi } from '../hooks/useAuth';
import config from '../config';

const SubAgentsList = ({ parentAgentId }) => {
  const [subAgents, setSubAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user } = useAuth();

  const fetchSubAgents = async () => {
    try {
      console.log('Fetching sub-agents from:', `${config.API_BASE_URL}/api/agents/sub-agents`);
      const response = await authApi.get('/api/agents/sub-agents');
      console.log('Sub-agents response:', response.data);
      setSubAgents(response.data.data);
    } catch (error) {
      console.error('Error fetching sub-agents:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch sub-agents',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubAgents();
  }, []);  // Remove toast from dependencies

  const handleStatusChange = async (agentId, newStatus) => {
    try {
      await authApi.patch(`/api/agents/sub-agents/${agentId}/status`, {
        status: newStatus,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast({
        title: 'Success',
        description: `Sub-agent ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      fetchSubAgents();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update sub-agent status',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCommissionUpdate = async (agentId, newRate) => {
    try {
      await authApi.patch(`/api/agents/sub-agents/${agentId}/commission`, {
        commissionRate: newRate,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast({
        title: 'Success',
        description: 'Commission rate updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      fetchSubAgents();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update commission rate',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (subAgents.length === 0) {
    return <Text>No sub-agents found</Text>;
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Agent ID</Th>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Status</Th>
            <Th>Joined Date</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {subAgents.map((agent) => (
            <Tr key={agent._id}>
              <Td>{agent.agentId}</Td>
              <Td>{agent.name}</Td>
              <Td>{agent.email}</Td>
              <Td>{agent.phone}</Td>
              <Td>
                <Badge colorScheme={agent.isApproved ? 'green' : 'yellow'}>
                  {agent.isApproved ? 'Approved' : 'Pending'}
                </Badge>
              </Td>
              <Td>{new Date(agent.createdAt).toLocaleDateString()}</Td>
              <Td>
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<FiMoreVertical />}
                    variant="ghost"
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem
                      icon={<FiDollarSign />}
                      onClick={() => {
                        setSelectedAgent(agent);
                        onOpen();
                      }}
                    >
                      Update Commission Rate
                    </MenuItem>
                    {agent.status === 'active' ? (
                      <MenuItem
                        icon={<FiUserX />}
                        onClick={() => handleStatusChange(agent.agentId, 'inactive')}
                        color="red.500"
                      >
                        Deactivate Agent
                      </MenuItem>
                    ) : (
                      <MenuItem
                        icon={<FiUserCheck />}
                        onClick={() => handleStatusChange(agent.agentId, 'active')}
                        color="green.500"
                      >
                        Activate Agent
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Commission Rate</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>Commission Rate (%)</FormLabel>
              <NumberInput
                defaultValue={selectedAgent?.commissionRate}
                min={0}
                max={100}
                precision={2}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={() => {
                const rate = document.querySelector('input[type="number"]').value;
                handleCommissionUpdate(selectedAgent.agentId, parseFloat(rate));
              }}
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SubAgentsList;
